import { supabase } from './supabase';

export type LegaProjectStatus = 'new' | 'contacted' | 'meeting_scheduled' | 'in_development' | 'completed' | 'archived';

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  created_at: string;
  updated_at: string;
}

export interface Project {
  id: string;
  lead_id: string;
  title: string;
  description: string;
  business_idea: string;
  status: LegaProjectStatus;
  meeting_date: string | null;
  created_at: string;
  updated_at: string;
  lead?: Lead;
}

export interface ChatMessage {
  role: 'assistant' | 'user';
  content: string;
  timestamp: string;
}

export interface ChatConversation {
  id: string;
  project_id: string;
  messages: ChatMessage[];
  lead_captured: boolean;
  created_at: string;
  updated_at: string;
}

export interface ProjectCanvas {
  id: string;
  project_id: string;
  value_proposition: string;
  target_audience: string;
  problem_solution: string;
  key_features: string[];
  mvp_scope: {
    features: Array<{
      id: string;
      description: string;
      priority: 'must-have' | 'nice-to-have';
      completed: boolean;
    }>;
    timeline: string;
    estimated_cost: number;
  };
  success_metrics: string[];
  ai_analysis: string;
  created_at: string;
  updated_at: string;
  project?: Project & { lead: Lead };
}

export const projectService = {
  // Criar um novo lead
  async createLead(
    name: string,
    email: string,
    phone: string
  ) {
    const { data: lead, error: leadError } = await supabase
      .from('leads')
      .insert({
        name,
        email,
        phone
      })
      .select()
      .single();

    if (leadError) throw leadError;
    return lead;
  },

  // Criar um novo projeto a partir de uma conversa
  async createProjectFromChat(
    title: string,
    businessIdea: string,
    messages: ChatMessage[],
    leadInfo?: {
      name: string;
      email: string;
      phone: string;
      id?: string; // ID do lead se já existir
    }
  ) {
    // Primeiro, criar ou obter o lead
    let leadId = leadInfo?.id;
    if (!leadId && leadInfo) {
      const lead = await this.createLead(leadInfo.name, leadInfo.email, leadInfo.phone);
      leadId = lead.id;
    }

    // Criar o projeto
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .insert({
        title,
        business_idea: businessIdea,
        lead_id: leadId,
        status: 'new'
      })
      .select()
      .single();

    if (projectError) throw projectError;

    // Criar a conversa
    const { error: chatError } = await supabase
      .from('chat_conversations')
      .insert({
        project_id: project.id,
        messages: JSON.stringify(messages),
        lead_captured: true
      });

    if (chatError) throw chatError;

    // Criar o canvas inicial
    const { error: canvasError } = await supabase
      .from('project_canvas')
      .insert({
        project_id: project.id,
        value_proposition: '',
        target_audience: '',
        problem_solution: '',
        key_features: [],
        mvp_scope: {
          features: [],
          timeline: '',
          estimated_cost: 0
        },
        success_metrics: [],
        ai_analysis: ''
      });

    if (canvasError) throw canvasError;

    return project;
  },

  // Listar todos os projetos com informações do lead
  async getProjects() {
    const { data, error } = await supabase
      .from('projects')
      .select('id, title, description, business_idea, status, meeting_date, created_at, updated_at, lead:leads(id, name, email, phone)')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data as (Project & { lead: Lead })[];
  },

  // Obter um projeto específico com sua conversa e lead
  async getProjectWithChat(projectId: string) {
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select(`
        *,
        lead:leads(*),
        chat:chat_conversations(*)
      `)
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    // Parse messages from JSONB to array
    if (project?.chat?.[0]?.messages) {
      try {
        const chatData = project.chat[0];
        chatData.messages = typeof chatData.messages === 'string'
          ? JSON.parse(chatData.messages)
          : chatData.messages;
        
        if (!Array.isArray(chatData.messages)) {
          chatData.messages = [];
        }
      } catch (e) {
        console.error('Error parsing chat messages:', e);
        project.chat[0].messages = [];
      }
    }

    return project;
  },

  // Atualizar status do projeto
  async updateProjectStatus(projectId: string, status: LegaProjectStatus) {
    const { data, error } = await supabase
      .from('projects')
      .update({ status })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Agendar reunião
  async scheduleMeeting(projectId: string, meetingDate: string) {
    const { data, error } = await supabase
      .from('projects')
      .update({
        status: 'meeting_scheduled',
        meeting_date: meetingDate
      })
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Atualizar informações do projeto
  async updateProject(projectId: string, updates: Partial<Project>) {
    const { data, error } = await supabase
      .from('projects')
      .update(updates)
      .eq('id', projectId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
};

export const projectCanvasService = {
  // Obter o canvas de um projeto
  async getCanvas(projectId: string) {
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('id, title, description, business_idea, status, meeting_date, created_at, updated_at, lead:leads(id, name, email, phone)')
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    const { data: canvas, error: canvasError } = await supabase
      .from('project_canvas')
      .select('id, project_id, value_proposition, target_audience, problem_solution, key_features, mvp_scope, success_metrics, ai_analysis, created_at, updated_at')
      .eq('project_id', projectId)
      .single();

    if (canvasError) {
      // Se não existir um canvas, criar um novo
      if (canvasError.code === 'PGRST116') {
        const newCanvas = {
          project_id: projectId,
          value_proposition: '',
          target_audience: '',
          problem_solution: '',
          key_features: [],
          mvp_scope: {
            features: [],
            timeline: '',
            estimated_cost: 0
          },
          success_metrics: [],
          ai_analysis: ''
        };

        const { data: createdCanvas, error: createError } = await supabase
          .from('project_canvas')
          .upsert(newCanvas)
          .select()
          .single();

        if (createError) throw createError;
        return { ...createdCanvas, project };
      }
      throw canvasError;
    }

    return { ...canvas, project };
  },

  // Atualizar o canvas
  async updateCanvas(projectId: string, updates: Partial<Omit<ProjectCanvas, 'id' | 'project_id' | 'created_at' | 'updated_at'>>) {
    try {
      // Primeiro, verificamos se o canvas existe
      const { data: existingCanvas } = await supabase
        .from('project_canvas')
        .select('*')
        .eq('project_id', projectId)
        .single();

      if (!existingCanvas) {
        // Se não existir, criamos um novo
        const { data: newCanvas, error: createError } = await supabase
          .from('project_canvas')
          .insert({
            project_id: projectId,
            ...updates
          })
          .select()
          .single();

        if (createError) throw createError;
        return newCanvas;
      }

      // Se existir, atualizamos
      const { data: updatedCanvas, error: updateError } = await supabase
        .from('project_canvas')
        .update(updates)
        .eq('project_id', projectId)
        .select()
        .single();

      if (updateError) throw updateError;
      return updatedCanvas;
    } catch (error) {
      console.error('Erro ao atualizar canvas:', error);
      throw error;
    }
  },

  // Obter projetos adjacentes (anterior e próximo)
  async getAdjacentProjects(projectId: string) {
    const { data: currentProject, error: currentError } = await supabase
      .from('projects')
      .select('created_at')
      .eq('id', projectId)
      .single();

    if (currentError) throw currentError;

    const { data: prev } = await supabase
      .from('projects')
      .select('id')
      .lt('created_at', currentProject.created_at)
      .order('created_at', { ascending: false })
      .limit(1)
      .single();

    const { data: next } = await supabase
      .from('projects')
      .select('id')
      .gt('created_at', currentProject.created_at)
      .order('created_at', { ascending: true })
      .limit(1)
      .single();

    return {
      prev: prev?.id,
      next: next?.id
    };
  }
};
